import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, Store, CreditCard, Copy, Link2, Clock } from "lucide-react";
import { brl } from "@/lib/format";

const PIX_TYPES = [
  { id: "cpf", label: "CPF" },
  { id: "cnpj", label: "CNPJ" },
  { id: "email", label: "E-mail" },
  { id: "phone", label: "Telefone" },
  { id: "random", label: "Aleatória" },
];

const DEPOSIT_TYPES = [
  { id: "none", label: "Não cobrar sinal" },
  { id: "percent", label: "Percentual do serviço" },
  { id: "fixed", label: "Valor fixo" },
];

const DAYS = [
  { key: "mon", label: "Segunda-feira" },
  { key: "tue", label: "Terça-feira" },
  { key: "wed", label: "Quarta-feira" },
  { key: "thu", label: "Quinta-feira" },
  { key: "fri", label: "Sexta-feira" },
  { key: "sat", label: "Sábado" },
  { key: "sun", label: "Domingo" },
];

const DEFAULT_WORKING_HOURS = DAYS.reduce((acc, day) => {
  acc[day.key] = { enabled: true, start: "09:00", end: "18:00" };
  return acc;
}, {});

function normalizeWorkingHours(value) {
  const source = value || {};
  return DAYS.reduce((acc, day) => {
    acc[day.key] = {
      enabled: source[day.key]?.enabled ?? DEFAULT_WORKING_HOURS[day.key].enabled,
      start: source[day.key]?.start || DEFAULT_WORKING_HOURS[day.key].start,
      end: source[day.key]?.end || DEFAULT_WORKING_HOURS[day.key].end,
    };
    return acc;
  }, {});
}

export default function Configuracoes() {
  const { studio, setStudio } = useAuth();
  const [tab, setTab] = useState("studio");
  const [form, setForm] = useState({
    name: "", bio: "", instagram: "", phone: "", whatsapp: "",
    address: "", logo_url: "",
    pix_key: "", pix_key_type: "random",
    deposit_type: "none", deposit_value: 30,
    avg_appointments_per_month: 60,
    working_hours: normalizeWorkingHours(),
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (studio) {
      setForm({
        name: studio.name || "",
        bio: studio.bio || "",
        instagram: studio.instagram || "",
        phone: studio.phone || "",
        whatsapp: studio.whatsapp || "",
        address: studio.address || "",
        logo_url: studio.logo_url || "",
        pix_key: studio.pix_key || "",
        pix_key_type: studio.pix_key_type || "random",
        deposit_type: studio.deposit_type || "none",
        deposit_value: studio.deposit_value || 30,
        avg_appointments_per_month: studio.avg_appointments_per_month || 60,
        working_hours: normalizeWorkingHours(studio.working_hours),
      });
    }
  }, [studio]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setWorkingDay = (day, patch) => {
    setForm(f => ({
      ...f,
      working_hours: {
        ...f.working_hours,
        [day]: { ...f.working_hours[day], ...patch },
      },
    }));
  };

  const save = async () => {
    setLoading(true);
    try {
      const { data } = await api.patch("/studio", form);
      setStudio(data);
      toast.success("Configurações salvas!");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const publicUrl = studio?.slug ? `${window.location.origin}/agendar/${studio.slug}` : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copiado!"); }
    catch { toast.error("Não foi possível copiar."); }
  };

  return (
    <div className="space-y-4" data-testid="settings-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Configurações</h1>
        <p className="text-[#71717A] text-sm mt-1">Studio, horários, pagamentos e informações públicas.</p>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="settings-tabs">
        <TabBtn active={tab === "studio"} onClick={() => setTab("studio")} icon={Store} label="Studio" tid="tab-studio" />
        <TabBtn active={tab === "horarios"} onClick={() => setTab("horarios")} icon={Clock} label="Horários" tid="tab-hours" />
        <TabBtn active={tab === "pagamentos"} onClick={() => setTab("pagamentos")} icon={CreditCard} label="Pagamentos" tid="tab-payments" />
      </div>

      {tab === "studio" && (
        <div className="space-y-3">
          <div className="card-nc p-4 space-y-3">
            <div className="flex items-center gap-3">
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="w-16 h-16 rounded-xl object-cover border border-[#E4E4E7]" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-[#F5ECE8] flex items-center justify-center text-[#682F3E] font-display font-bold text-xl">
                  {form.name.slice(0, 1).toUpperCase() || "S"}
                </div>
              )}
              <div className="flex-1">
                <Field label="URL do logo (opcional)">
                  <input data-testid="s-logo" value={form.logo_url} onChange={e => set("logo_url", e.target.value)}
                    placeholder="https://…" className="input-nc" />
                </Field>
              </div>
            </div>
            <Field label="Nome do studio">
              <input data-testid="s-name" value={form.name} onChange={e => set("name", e.target.value)} className="input-nc" />
            </Field>
            <Field label="Bio (aparece na página pública)">
              <textarea data-testid="s-bio" rows={3} value={form.bio} onChange={e => set("bio", e.target.value)} className="input-nc"
                placeholder="Conte em poucas palavras sobre você e seu trabalho." />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Instagram (sem @)">
                <input data-testid="s-ig" value={form.instagram} onChange={e => set("instagram", e.target.value)} className="input-nc" placeholder="bellanails" />
              </Field>
              <Field label="WhatsApp">
                <input data-testid="s-wa" value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)} className="input-nc" placeholder="5511..." />
              </Field>
            </div>
            <Field label="Endereço"><input data-testid="s-addr" value={form.address} onChange={e => set("address", e.target.value)} className="input-nc" /></Field>
            <Field label="Média de atendimentos/mês">
              <input data-testid="s-avg" type="number" inputMode="numeric" value={form.avg_appointments_per_month}
                onChange={e => set("avg_appointments_per_month", Number(e.target.value) || 0)} className="input-nc" />
            </Field>
          </div>

          {publicUrl && (
            <div className="card-nc p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-[#682F3E] mb-1 flex items-center gap-1">
                <Link2 size={12} /> Seu link público
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 text-sm font-mono-brand text-[#27272A] truncate">{publicUrl}</div>
                <button onClick={copyLink} data-testid="s-copy-link" className="btn-primary-nc py-2 px-3 text-xs">
                  <Copy size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "horarios" && (
        <div className="card-nc p-4 space-y-4" data-testid="working-hours-card">
          <div>
            <h3 className="font-semibold text-[#27272A]">Horários de atendimento</h3>
            <p className="text-xs text-[#71717A] mt-1">
              Estes horários controlam o autoagendamento. A cliente verá somente horários livres dentro dos períodos que você definir.
            </p>
          </div>

          <div className="space-y-2">
            {DAYS.map(day => {
              const config = form.working_hours[day.key];
              return (
                <div key={day.key} className="rounded-xl border border-[#E4E4E7] p-3" data-testid={`hours-${day.key}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold text-sm text-[#27272A]">{day.label}</div>
                    <button
                      type="button"
                      onClick={() => setWorkingDay(day.key, { enabled: !config.enabled })}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
                        config.enabled ? "bg-[#EBF2EE] text-[#365F48]" : "bg-[#F5ECE8] text-[#A15E6D]"
                      }`}
                    >
                      {config.enabled ? "Atende" : "Fechado"}
                    </button>
                  </div>

                  {config.enabled && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <Field label="Das">
                        <input
                          type="time"
                          step="1800"
                          value={config.start}
                          onChange={e => setWorkingDay(day.key, { start: e.target.value })}
                          className="input-nc"
                          data-testid={`hours-${day.key}-start`}
                        />
                      </Field>
                      <Field label="Até">
                        <input
                          type="time"
                          step="1800"
                          value={config.end}
                          onChange={e => setWorkingDay(day.key, { end: e.target.value })}
                          className="input-nc"
                          data-testid={`hours-${day.key}-end`}
                        />
                      </Field>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="p-3 rounded-xl bg-[#F5ECE8] text-xs text-[#682F3E]">
            Os horários do autoagendamento são oferecidos em intervalos de 30 minutos e respeitam a duração de cada serviço.
          </div>
        </div>
      )}

      {tab === "pagamentos" && (
        <div className="space-y-3">
          <div className="card-nc p-4 space-y-3">
            <h3 className="font-semibold text-[#27272A]">Chave Pix</h3>
            <p className="text-xs text-[#71717A]">A chave é exibida somente depois que a cliente confirma uma reserva com sinal. Por privacidade, prefira uma chave Pix aleatória em vez de CPF ou telefone.</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo da chave">
                <select data-testid="s-pix-type" value={form.pix_key_type} onChange={e => set("pix_key_type", e.target.value)} className="input-nc">
                  {PIX_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Chave Pix">
                <input data-testid="s-pix-key" value={form.pix_key} onChange={e => set("pix_key", e.target.value)} className="input-nc" />
              </Field>
            </div>
          </div>

          <div className="card-nc p-4 space-y-3">
            <h3 className="font-semibold text-[#27272A]">Sinal de reserva</h3>
            <p className="text-xs text-[#71717A]">Cobre um sinal para clientes travarem o horário no autoagendamento e reduza faltas.</p>
            <div className="grid grid-cols-3 gap-2">
              {DEPOSIT_TYPES.map(t => (
                <button key={t.id} onClick={() => set("deposit_type", t.id)} data-testid={`dep-${t.id}`}
                  className={`p-3 rounded-xl border-2 text-xs font-semibold text-left transition ${form.deposit_type === t.id ? "border-[#682F3E] bg-[#F5ECE8] text-[#682F3E]" : "border-[#E4E4E7] text-[#71717A]"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            {form.deposit_type !== "none" && (
              <Field label={form.deposit_type === "percent" ? "Percentual (%)" : "Valor fixo (R$)"}>
                <input data-testid="s-dep-value" type="number" inputMode="decimal" value={form.deposit_value}
                  onChange={e => set("deposit_value", Number(e.target.value))} className="input-nc" />
              </Field>
            )}
            {form.deposit_type === "percent" && form.deposit_value > 0 && (
              <div className="p-3 rounded-xl bg-[#F5ECE8] text-xs text-[#682F3E]">
                Exemplo: um serviço de {brl(150)} exigirá um sinal de <b>{brl(150 * form.deposit_value / 100)}</b>.
              </div>
            )}
            {form.deposit_type !== "none" && !form.pix_key && (
              <div className="p-3 rounded-xl bg-[#F9ECE8] text-xs text-[#B76E56]">
                Configure sua chave Pix acima para que o sinal seja exibido ao cliente.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="sticky bottom-20 lg:bottom-0 lg:relative pt-2">
        <button data-testid="s-save" onClick={save} disabled={loading}
          className="btn-primary-nc w-full flex items-center justify-center gap-2 shadow-lg">
          <Save size={16} /> {loading ? "Salvando…" : "Salvar configurações"}
        </button>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, tid }) {
  return (
    <button onClick={onClick} data-testid={tid}
      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs md:text-sm font-semibold transition ${active ? "bg-[#682F3E] text-white" : "bg-white text-[#71717A] border border-[#E4E4E7]"}`}>
      <Icon size={16} /> {label}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[#71717A] mb-1">{label}</label>
      {children}
    </div>
  );
}
